import { useState } from 'react';
import { Typography } from 'antd';
import { PlusOutlined, TagOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type {
  ProviderService,
  ServiceType,
} from '../../../types/provider';
import type { LanguageCode } from '../../../types/i18n';
import { pickTranslatedName } from '../../../utils/i18nDisplay';
import { SectionShell } from './SectionShell';
import { ServiceRow } from './ServiceRow';

const { Text } = Typography;

interface ServicesSectionProps {
  services: ProviderService[];
  serviceTypes: ServiceType[];
  currentLanguage: LanguageCode;
  defaultLanguage: LanguageCode;
  onAddService: () => void;
  onEditService: (service: ProviderService) => void;
  onToggleService: (service: ProviderService) => void;
}

export function ServicesSection({
  services,
  serviceTypes,
  currentLanguage,
  defaultLanguage,
  onAddService,
  onEditService,
  onToggleService,
}: ServicesSectionProps) {
  const { t } = useTranslation('provider');

  const bookingModeLabel = (mode: string) =>
    t(`profile.bookingMode.${mode}`, { defaultValue: mode });

  const resolveDisplayName = (svc: ProviderService): string => {
    if (svc.serviceTypeId !== null) {
      const parent = serviceTypes.find((tp) => tp.id === svc.serviceTypeId);
      if (parent) {
        return pickTranslatedName(parent, currentLanguage, defaultLanguage) || svc.name;
      }
    }
    return svc.name;
  };

  const usedIds = new Set(services.map((s) => s.serviceTypeId).filter(Boolean));
  const canAddMore = serviceTypes.some((tp) => tp.active && !usedIds.has(tp.id));
  const activeCount = services.filter((s) => s.active).length;

  const countExtra =
    services.length > 0 ? (
      <span
        className="bl-tabular-nums"
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--bl-accent, #3390ec)',
          background: 'var(--bl-accent-soft, rgba(51,144,236,0.10))',
          border: '1px solid rgba(51,144,236,0.18)',
          borderRadius: 999,
          padding: '2px 10px',
          letterSpacing: 0.2,
        }}
      >
        {t('profile.services.openCount', {
          active: activeCount,
          total: services.length,
        })}
      </span>
    ) : null;

  return (
    <SectionShell
      caption={t('profile.sections.servicesCaption')}
      icon={<TagOutlined />}
      iconColor="#3390ec"
      extra={countExtra}
      padded={services.length > 0 || canAddMore}
    >
      {services.length === 0 ? (
        <EmptyState canAddMore={canAddMore} onAddService={onAddService} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {services.map((service) => (
            <ServiceRow
              key={service.id}
              service={service}
              displayName={resolveDisplayName(service)}
              bookingModeLabel={bookingModeLabel(service.bookingMode)}
              onEdit={onEditService}
              onToggle={onToggleService}
            />
          ))}
          {canAddMore && <AddServiceButton onClick={onAddService} />}
        </div>
      )}
    </SectionShell>
  );
}

function EmptyState({
  canAddMore,
  onAddService,
}: {
  canAddMore: boolean;
  onAddService: () => void;
}) {
  const { t } = useTranslation('provider');
  return (
    <div
      style={{
        padding: '24px 16px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        textAlign: 'center',
      }}
    >
      <div
        aria-hidden
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background:
            'linear-gradient(135deg, rgba(51,144,236,0.14) 0%, rgba(51,144,236,0.28) 100%)',
          border: '1px solid rgba(51,144,236,0.20)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--bl-accent, #3390ec)',
          fontSize: 24,
          boxShadow: '0 6px 18px rgba(51,144,236,0.10)',
        }}
      >
        <TagOutlined />
      </div>
      <Text
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: 'var(--bl-text-strong, #0f172a)',
        }}
      >
        {t('profile.services.empty')}
      </Text>
      <Text
        type="secondary"
        style={{ fontSize: 12, lineHeight: 1.45, maxWidth: 260 }}
      >
        {t('profile.services.emptyHint')}
      </Text>
      {canAddMore && <AddServiceButton onClick={onAddService} />}
    </div>
  );
}

function AddServiceButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('provider');
  const [pressed, setPressed] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        width: '100%',
        minHeight: 44,
        marginTop: 4,
        padding: '0 16px',
        border: '1px dashed rgba(51,144,236,0.38)',
        borderRadius: 12,
        background: 'rgba(51,144,236,0.04)',
        color: 'var(--bl-accent, #3390ec)',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        transform: pressed ? 'scale(0.985)' : 'scale(1)',
        transition: 'transform 0.1s ease, background 0.15s ease',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        fontFamily: 'inherit',
        letterSpacing: 0.1,
      }}
    >
      <PlusOutlined />
      <span>{t('profile.services.addBtn')}</span>
    </button>
  );
}
